import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Spinner, Button, Input, Textarea, Switch, Chip } from '@nextui-org/react';
import {
  ArrowLeft,
  Save,
  MapPin,
  Plus,
  Trash2,
  Upload,
  Image as ImageIcon,
  Phone,
  Mail,
  Lightbulb,
  CheckCircle2,
  TrendingUp,
  X,
} from 'lucide-react';
import clubService, { Club, ClubAddress, SocialMediaCollection } from '../services/clubService';

const TEAL = '#3FCED2';

interface QualityCriterion {
  key: string;
  label: string;
  tip: string;
  points: number;
  met: boolean;
}

interface QualityScore {
  score: number;
  maxScore: number;
  percentage: number;
  level: 'excellent' | 'good' | 'fair' | 'poor' | 'very_poor';
  criteria: QualityCriterion[];
}

const qualityColor = (lvl: QualityScore['level']) => {
  switch (lvl) {
    case 'excellent': return '#10B981';
    case 'good': return '#3B82F6';
    case 'fair': return '#F59E0B';
    case 'poor': return '#EF4444';
    default: return '#6B7280';
  }
};

const qualityLabel = (lvl: QualityScore['level']) => {
  switch (lvl) {
    case 'excellent': return 'Ausgezeichnet';
    case 'good': return 'Gut';
    case 'fair': return 'Ausbaufähig';
    case 'poor': return 'Verbesserungswürdig';
    default: return 'Unvollständig';
  }
};

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const EditClubPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [club, setClub] = useState<Club | null>(null);
  const [changed, setChanged] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [description, setDescription] = useState('');
  const [edart, setEdart] = useState(false);
  const [steeldart, setSteeldart] = useState(false);
  const [membershipInfo, setMembershipInfo] = useState('');
  const [membershipLink, setMembershipLink] = useState('');
  const [address, setAddress] = useState<ClubAddress | null>(null);
  const [associations, setAssociations] = useState<string[]>([]);
  const [leagues, setLeagues] = useState<string[]>([]);
  const [socialMedia, setSocialMedia] = useState<SocialMediaCollection>({});

  const fetchClub = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await clubService.getClubById(id);
      const c = (res as any)?.data ?? res;
      if (!c) {
        setMessage({ type: 'error', text: 'Verein konnte nicht geladen werden' });
        return;
      }
      setClub(c);
      setName(c.name ?? '');
      setEmail(c.email ?? '');
      setPhone(c.phone ?? '');
      setDescription(c.description ?? '');
      setEdart(c.edart ?? false);
      setSteeldart(c.steeldart ?? false);
      setMembershipInfo(c.membershipInformation ?? '');
      setMembershipLink(c.membershipLink ?? '');
      setAddress(c.address ?? null);
      setAssociations(c.associations ?? []);
      setLeagues(c.leagues ?? []);
      setSocialMedia(c.socialMediaCollection ?? {});
      setChanged(false);
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Verein konnte nicht geladen werden' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchClub();
    window.scrollTo({ top: 0 });
  }, [fetchClub]);

  const markChanged = () => setChanged(true);

  const qualityScore: QualityScore = useMemo(() => {
    const criteria: QualityCriterion[] = [];
    let score = 0;
    let maxScore = 0;

    const hasMainImage = !!(club?.image && club.image.length > 0);
    criteria.push({ key: 'hasMainImage', label: 'Header-Bild', tip: 'Lade ein Header-Bild hoch, um deinen Verein attraktiver zu präsentieren', points: 20, met: hasMainImage });
    maxScore += 20; if (hasMainImage) score += 20;

    const hasMultipleImages = !!(club?.images && club.images.length >= 3);
    criteria.push({ key: 'hasMultipleImages', label: 'Bildergalerie (mind. 3)', tip: 'Füge mindestens 3 Bilder hinzu, um Interessenten einen besseren Eindruck zu geben', points: 15, met: hasMultipleImages });
    maxScore += 15; if (hasMultipleImages) score += 15;

    const hasLogo = !!(club?.logo && club.logo.length > 0);
    criteria.push({ key: 'hasLogo', label: 'Vereinslogo', tip: 'Ein Logo macht deinen Verein wiedererkennbar', points: 10, met: hasLogo });
    maxScore += 10; if (hasLogo) score += 10;

    const hasEmail = email.length > 0;
    criteria.push({ key: 'hasEmail', label: 'E-Mail-Adresse', tip: 'Hinterlege eine E-Mail für Kontaktanfragen', points: 10, met: hasEmail });
    maxScore += 10; if (hasEmail) score += 10;

    const hasPhone = phone.length > 0;
    criteria.push({ key: 'hasPhone', label: 'Telefonnummer', tip: 'Eine Telefonnummer ermöglicht schnelle Kontaktaufnahme', points: 10, met: hasPhone });
    maxScore += 10; if (hasPhone) score += 10;

    const hasAddress = !!(address && address.latitude != null && address.longitude != null);
    criteria.push({ key: 'hasAddress', label: 'Vollständige Adresse', tip: 'Eine Adresse hilft Spielern, deinen Verein zu finden', points: 15, met: hasAddress });
    maxScore += 15; if (hasAddress) score += 15;

    const hasDescription = description.trim().length > 0;
    criteria.push({ key: 'hasDescription', label: 'Beschreibung', tip: 'Eine Beschreibung informiert über deinen Verein', points: 15, met: hasDescription });
    maxScore += 15; if (hasDescription) score += 15;

    const hasOpenTraining = !!(club?.openTraining && Object.values(club.openTraining).some((d: any) => Array.isArray(d) && d.length > 0));
    criteria.push({ key: 'hasOpenTraining', label: 'Offenes Training', tip: 'Trainingszeiten helfen Interessenten, vorbeizukommen', points: 10, met: hasOpenTraining });
    maxScore += 10; if (hasOpenTraining) score += 10;

    const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
    let level: QualityScore['level'];
    if (percentage >= 85) level = 'excellent';
    else if (percentage >= 70) level = 'good';
    else if (percentage >= 50) level = 'fair';
    else if (percentage >= 30) level = 'poor';
    else level = 'very_poor';

    return { score, maxScore, percentage, level, criteria };
  }, [club, email, phone, address, description]);

  const handleSave = async () => {
    if (!name.trim()) {
      setMessage({ type: 'error', text: 'Bitte einen Namen festlegen' });
      return;
    }
    if (!club?._id && !club?.id) return;
    setSaving(true);
    setMessage(null);
    try {
      await clubService.updateClub({
        id: club._id ?? club.id,
        name,
        email: email.trim() === '' ? null : email,
        phone: phone.trim() === '' ? null : phone,
        description: description.trim() === '' ? null : description,
        edart,
        steeldart,
        membershipInformation: membershipInfo.trim() === '' ? null : membershipInfo,
        membershipLink: membershipLink.trim() === '' ? null : membershipLink,
        addressCountry: address?.country ?? null,
        addressLabel: address?.label ?? null,
        addressLatitude: address?.latitude ?? null,
        addressLongitude: address?.longitude ?? null,
        associations,
        leagues,
        socialMediaCollection: socialMedia,
      });
      setChanged(false);
      setMessage({ type: 'success', text: 'Erfolgreich aktualisiert' });
      await fetchClub();
    } catch (err: any) {
      console.error(err);
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Aktualisierung fehlgeschlagen' });
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (changed) {
      if (!window.confirm('Deine Änderungen wurden noch nicht gespeichert. Wirklich abbrechen?')) return;
    }
    navigate('/verwaltung?tab=clubs');
  };

  const handleFileUpload = async (field: 'logo' | 'image', file: File) => {
    if (!club?._id && !club?.id) return;
    if (file.size > 20 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Datei zu groß (Max. 20 MB)' });
      return;
    }
    if (field === 'logo' && !file.name.toLowerCase().endsWith('.png')) {
      setMessage({ type: 'error', text: 'Logo muss eine .png Datei sein' });
      return;
    }
    setSaving(true);
    try {
      const base64 = await fileToBase64(file);
      await clubService.updateClub({ id: club._id ?? club.id, [field]: base64 });
      await fetchClub();
      setMessage({ type: 'success', text: `${field === 'logo' ? 'Logo' : 'Header'} aktualisiert` });
    } catch (err: any) {
      console.error(err);
      setMessage({ type: 'error', text: 'Upload fehlgeschlagen' });
    } finally {
      setSaving(false);
    }
  };

  const handleFileRemove = async (field: 'logo' | 'image') => {
    if (!club?._id && !club?.id) return;
    if (!window.confirm('Wirklich entfernen?')) return;
    setSaving(true);
    try {
      await clubService.updateClub({ id: club._id ?? club.id, [field]: null });
      await fetchClub();
      setMessage({ type: 'success', text: 'Erfolgreich entfernt' });
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Entfernen fehlgeschlagen' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddImage = async (file: File) => {
    if (!club?._id && !club?.id) return;
    if (file.size > 20 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Datei zu groß (Max. 20 MB)' });
      return;
    }
    setSaving(true);
    try {
      const base64 = await fileToBase64(file);
      await clubService.addImage((club._id ?? club.id) as string, base64);
      await fetchClub();
      setMessage({ type: 'success', text: 'Bild hinzugefügt' });
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Bild konnte nicht hochgeladen werden' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveImage = async (img: string) => {
    if (!club?._id && !club?.id) return;
    if (!window.confirm('Bild wirklich entfernen?')) return;
    setSaving(true);
    try {
      await clubService.removeImage((club._id ?? club.id) as string, img);
      await fetchClub();
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Bild konnte nicht entfernt werden' });
    } finally {
      setSaving(false);
    }
  };

  const addItem = (list: string[], setList: (l: string[]) => void, prompt: string) => {
    const value = window.prompt(prompt);
    if (value && value.trim()) {
      setList([...list, value.trim()]);
      markChanged();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Spinner color="primary" size="lg" label="Verein laden..." />
      </div>
    );
  }

  if (!club) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-4">
        <p className="text-zinc-400 mb-4">Verein konnte nicht geladen werden.</p>
        <Button onPress={handleBack} startContent={<ArrowLeft size={16} />}>Zurück</Button>
      </div>
    );
  }

  const qColor = qualityColor(qualityScore.level);
  const qLabel = qualityLabel(qualityScore.level);
  const met = qualityScore.criteria.filter((c) => c.met);
  const unmet = qualityScore.criteria.filter((c) => !c.met);

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Helmet>
        <title>{name || 'Verein'} bearbeiten - DartCloud</title>
      </Helmet>

      {/* Header */}
      <div className="sticky top-0 z-10 bg-black/80 backdrop-blur border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button onClick={handleBack} className="text-zinc-300 hover:text-white p-1">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-bold flex-1 truncate">Verein bearbeiten</h1>
          {changed && (
            <Chip size="sm" color="warning" variant="flat">Ungespeichert</Chip>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-8">
        {/* Message */}
        {message && (
          <div
            className={`rounded-xl p-3 text-sm flex items-start gap-2 ${
              message.type === 'success'
                ? 'bg-green-500/10 border border-green-500/30 text-green-300'
                : 'bg-red-500/10 border border-red-500/30 text-red-300'
            }`}
          >
            <span className="flex-1">{message.text}</span>
            <button onClick={() => setMessage(null)} className="text-current opacity-60 hover:opacity-100">
              <X size={14} />
            </button>
          </div>
        )}

        {/* Quality Score Card */}
        <section
          className="rounded-2xl border overflow-hidden"
          style={{ borderColor: `${qColor}55`, boxShadow: `0 4px 12px ${qColor}1a` }}
        >
          <div className="p-5 flex items-center gap-5" style={{ backgroundColor: `${qColor}1a` }}>
            <div className="relative w-20 h-20">
              <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="34" fill="none" stroke="#27272a" strokeWidth="8" />
                <circle
                  cx="40" cy="40" r="34"
                  fill="none" stroke={qColor} strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${(qualityScore.percentage / 100) * 213.6} 213.6`}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center text-xl font-bold" style={{ color: qColor }}>
                {qualityScore.percentage}%
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-sm text-zinc-300">
                <TrendingUp size={16} style={{ color: qColor }} />
                <span className="font-medium">Sichtbarkeits-Score</span>
              </div>
              <div className="text-2xl font-bold mt-1" style={{ color: qColor }}>{qLabel}</div>
              <p className="text-xs text-zinc-400 mt-1">Je höher der Score, desto besser wird dein Verein in Listen geranked</p>
            </div>
          </div>
          <div className="p-5 space-y-4">
            {met.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-white">
                  <CheckCircle2 size={16} className="text-emerald-500" />
                  Erfüllt ({met.length}/{qualityScore.criteria.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {met.map((c) => (
                    <span key={c.key} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400">
                      <CheckCircle2 size={12} /> {c.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {unmet.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-white">
                  <Lightbulb size={16} className="text-amber-500" />
                  Verbesserungspotenzial
                </div>
                <div className="space-y-2">
                  {unmet.slice(0, 3).map((c) => (
                    <div key={c.key} className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3 flex items-start gap-3">
                      <span className="text-xs font-bold px-2 py-1 rounded bg-amber-500/10 text-amber-400">+{c.points}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold">{c.label}</div>
                        <div className="text-xs text-zinc-400 mt-0.5">{c.tip}</div>
                      </div>
                    </div>
                  ))}
                  {unmet.length > 3 && (
                    <p className="text-xs text-zinc-500 italic">+{unmet.length - 3} weitere Verbesserungsmöglichkeiten</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Name */}
        <section>
          <h2 className="text-base font-semibold mb-2">Name</h2>
          <Input
            value={name}
            onValueChange={(v) => { setName(v); markChanged(); }}
            placeholder="Name"
            classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }}
          />
        </section>

        {/* Spielmöglichkeiten */}
        <section>
          <h2 className="text-base font-semibold mb-2">Spielmöglichkeiten</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
              <span className="text-sm">Steeldart</span>
              <Switch isSelected={steeldart} onValueChange={(v) => { setSteeldart(v); markChanged(); }} />
            </div>
            <div className="flex items-center justify-between bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
              <span className="text-sm">E-Dart</span>
              <Switch isSelected={edart} onValueChange={(v) => { setEdart(v); markChanged(); }} />
            </div>
          </div>
        </section>

        {/* Kontakt */}
        <section>
          <h2 className="text-base font-semibold mb-2">Kontakt</h2>
          <div className="space-y-3">
            <Input
              value={email}
              onValueChange={(v) => { setEmail(v); markChanged(); }}
              placeholder="Email"
              type="email"
              startContent={<Mail size={16} className="text-zinc-400" />}
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }}
            />
            <Input
              value={phone}
              onValueChange={(v) => { setPhone(v); markChanged(); }}
              placeholder="Telefon"
              startContent={<Phone size={16} className="text-zinc-400" />}
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }}
            />
          </div>
        </section>

        {/* Adresse */}
        <section>
          <h2 className="text-base font-semibold mb-2">Anfahrt</h2>
          {address ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3 flex items-center gap-3">
              <MapPin size={18} className="text-zinc-400 flex-shrink-0" />
              <span className="flex-1 text-sm truncate">{address.label || 'Keine Adresse'}</span>
              <Button
                isIconOnly size="sm" variant="light" color="danger"
                onPress={() => { setAddress(null); markChanged(); }}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          ) : (
            <p className="text-sm text-zinc-400">Keine Adresse hinterlegt. Adressen können aktuell nur in der Mobile-App geändert werden.</p>
          )}
        </section>

        {/* Mitgliedschaft */}
        <section>
          <h2 className="text-base font-semibold mb-2">Informationen zur Mitgliedschaft</h2>
          <Textarea
            value={membershipInfo}
            onValueChange={(v) => { setMembershipInfo(v); markChanged(); }}
            placeholder="Informationen zur Mitgliedschaft"
            minRows={6}
            classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }}
          />
          <div className="mt-3">
            <Input
              value={membershipLink}
              onValueChange={(v) => { setMembershipLink(v); markChanged(); }}
              placeholder="Link zum Mitgliedschaftsantrag"
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }}
            />
          </div>
        </section>

        {/* Beschreibung */}
        <section>
          <h2 className="text-base font-semibold mb-2">Beschreibung</h2>
          <Textarea
            value={description}
            onValueChange={(v) => { setDescription(v); markChanged(); }}
            placeholder="Beschreibung deines Vereins"
            minRows={6}
            classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }}
          />
        </section>

        {/* Verbände */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-base font-semibold">Verbände</h2>
            <Button size="sm" variant="flat" startContent={<Plus size={14} />}
              onPress={() => addItem(associations, setAssociations, 'Verband:')}
            >
              Hinzufügen
            </Button>
          </div>
          {associations.length === 0 ? (
            <p className="text-sm text-zinc-500">Keine Verbände hinzugefügt.</p>
          ) : (
            <div className="space-y-2">
              {associations.map((a, idx) => (
                <div key={`${a}-${idx}`} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3 flex items-center gap-3">
                  <span className="flex-1 text-sm">{a}</span>
                  <Button isIconOnly size="sm" variant="light" color="danger"
                    onPress={() => { setAssociations(associations.filter((_, i) => i !== idx)); markChanged(); }}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Ligen */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-base font-semibold">Ligen</h2>
            <Button size="sm" variant="flat" startContent={<Plus size={14} />}
              onPress={() => addItem(leagues, setLeagues, 'Liga:')}
            >
              Hinzufügen
            </Button>
          </div>
          {leagues.length === 0 ? (
            <p className="text-sm text-zinc-500">Keine Ligen hinzugefügt.</p>
          ) : (
            <div className="space-y-2">
              {leagues.map((l, idx) => (
                <div key={`${l}-${idx}`} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3 flex items-center gap-3">
                  <span className="flex-1 text-sm">{l}</span>
                  <Button isIconOnly size="sm" variant="light" color="danger"
                    onPress={() => { setLeagues(leagues.filter((_, i) => i !== idx)); markChanged(); }}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Social Media */}
        <section>
          <h2 className="text-base font-semibold mb-2">Social Media</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(['website', 'instagram', 'facebook', 'twitter', 'tiktok', 'youtube'] as const).map((k) => (
              <Input
                key={k}
                value={(socialMedia as any)[k] ?? ''}
                onValueChange={(v) => { setSocialMedia({ ...socialMedia, [k]: v }); markChanged(); }}
                placeholder={k.charAt(0).toUpperCase() + k.slice(1)}
                classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }}
              />
            ))}
          </div>
        </section>

        {/* Logo */}
        <section>
          <h2 className="text-base font-semibold mb-1">Logo</h2>
          <p className="text-xs text-zinc-400 mb-3">Logo deines Vereins (.png)</p>
          {club.logo && (
            <div className="mb-3">
              <div className="h-32 bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden flex items-center justify-center">
                <img src={clubService.buildFileUrl(club.logo) ?? ''} alt="Logo" className="h-full object-contain" />
              </div>
              <Button size="sm" variant="light" color="danger" className="mt-2"
                onPress={() => handleFileRemove('logo')}
                startContent={<Trash2 size={14} />}
              >
                Logo entfernen
              </Button>
            </div>
          )}
          <label className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 rounded-xl cursor-pointer text-sm">
            <Upload size={14} />
            Logo hochladen
            <input type="file" accept="image/png" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload('logo', f); e.target.value = ''; }}
            />
          </label>
        </section>

        {/* Header-Bild */}
        <section>
          <h2 className="text-base font-semibold mb-1">Header-Bild</h2>
          <p className="text-xs text-zinc-400 mb-3">Header deiner Vereins-Seite</p>
          {club.image && (
            <div className="mb-3">
              <div className="h-40 bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
                <img src={clubService.buildFileUrl(club.image) ?? ''} alt="Header" className="w-full h-full object-cover" />
              </div>
              <Button size="sm" variant="light" color="danger" className="mt-2"
                onPress={() => handleFileRemove('image')}
                startContent={<Trash2 size={14} />}
              >
                Header entfernen
              </Button>
            </div>
          )}
          <label className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 rounded-xl cursor-pointer text-sm">
            <Upload size={14} />
            Header hochladen
            <input type="file" accept="image/*" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload('image', f); e.target.value = ''; }}
            />
          </label>
        </section>

        {/* Bildergalerie */}
        <section>
          <h2 className="text-base font-semibold mb-3">Bilder</h2>
          {(!club.images || club.images.length === 0) ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-sm text-zinc-400 flex items-center gap-2">
              <ImageIcon size={16} /> Keine Bilder
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {club.images.map((img) => (
                <div key={img} className="relative aspect-square rounded-xl overflow-hidden border border-zinc-800 group">
                  <img src={clubService.buildFileUrl(img) ?? ''} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => handleRemoveImage(img)}
                    className="absolute top-2 right-2 bg-black/70 hover:bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <label className="inline-flex items-center gap-2 px-4 py-2 mt-3 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 rounded-xl cursor-pointer text-sm">
            <Plus size={14} />
            Bild hinzufügen
            <input type="file" accept="image/*" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleAddImage(f); e.target.value = ''; }}
            />
          </label>
        </section>
      </div>

      {/* Sticky Save Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur border-t border-zinc-800 z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3">
          <Button
            className="w-full"
            color="primary"
            isDisabled={!changed || saving}
            isLoading={saving}
            onPress={handleSave}
            startContent={!saving ? <Save size={16} /> : null}
            style={{ backgroundColor: TEAL, color: '#000' }}
          >
            Aktualisieren
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditClubPage;
